import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, Ship, TrendingUp, AlertTriangle } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article4 = () => {
  const { t, language } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="outline" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.backToHome')}
            </Button>
          </Link>
          
          <div className="flex items-center mb-4">
            <Badge className="bg-blue-600 text-white mr-4">
              <Ship className="h-3 w-3 mr-1" />
              Shipping
            </Badge>
            <span className="text-sm text-gray-600">Kuehne+Nagel News</span>
          </div>
          
          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            February 2026
            <span className="mx-2">•</span>
            <span>6 min read</span>
          </div>
          
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.4.title')}
          </h1>
          
          <img 
            src="https://imgs.newseditor.aws.kuehne-nagel.cloud/3e4a56ae-0a48-4c6e-90e9-3633e827e3e6.jpg" 
            alt="Gemini Cooperation"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.4.intro')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.4.section1.title')}</h2>
            
            <p className="mb-6">
              {t('article.4.p1')}
            </p>

            <p className="mb-6">
              {t('article.4.p2')}
            </p>

            <div className="bg-blue-50 p-6 rounded-lg my-8 border border-blue-100">
              <h3 className="font-bold text-blue-900 mb-2 flex items-center">
                <TrendingUp className="mr-2" />
                {t('article.4.highlight.title')}
              </h3>
              <p className="text-sm text-blue-800">
                {t('article.4.highlight.content')}
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.4.section2.title')}</h2>
            
            <p className="mb-6">
              {t('article.4.p3')}
            </p>

            <p className="mb-6">
              {t('article.4.p4')}
            </p>

            <div className="bg-red-50 p-6 rounded-lg my-8">
              <h3 className="font-bold text-red-900 mb-4">{language === 'ko' ? '재정적 압박과 도전' : 'Financial Pressures and Challenges'}</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-red-800">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <AlertTriangle className="mr-1 h-4 w-4" />
                    {language === 'ko' ? 'Maersk 재정 어려움' : 'Maersk Financial Difficulties'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '분기 손실:' : 'Quarterly Loss:'}</strong> {language === 'ko' ? '2년 만에 첫 손실' : 'First loss in two years'}</li>
                    <li><strong>{language === 'ko' ? '인력 감축:' : 'Job Cuts:'}</strong> {language === 'ko' ? '2026년 1,000명 감축' : '1,000 job reduction for 2026'}</li>
                    <li><strong>{language === 'ko' ? '과잉 용량:' : 'Overcapacity:'}</strong> {language === 'ko' ? '4-8% 과잉 용량 예상' : '4-8% overcapacity expected'}</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <TrendingUp className="mr-1 h-4 w-4" />
                    {language === 'ko' ? 'Hapag-Lloyd 성과' : 'Hapag-Lloyd Performance'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '2025년 매출:' : '2025 Revenue:'}</strong> {language === 'ko' ? 'USD 211억' : 'USD 21.1 billion'}</li>
                    <li><strong>{language === 'ko' ? 'EBIT:' : 'EBIT:'}</strong> {language === 'ko' ? 'USD 11억' : 'USD 1.1 billion'}</li>
                    <li><strong>{language === 'ko' ? 'ZIM 인수:' : 'ZIM Acquisition:'}</strong> {language === 'ko' ? '제미니 협력 강화 예상' : 'Expected to strengthen Gemini'}</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.4.section3.title')}</h2>
            
            <p className="mb-6">
              {t('article.4.p5')}
            </p>

            <p className="mb-6">
              {t('article.4.p6')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.4.section4.title')}</h2>
            
            <p className="mb-6">
              {t('article.4.p7')}
            </p>

            <p className="mb-6">
              {t('article.4.p8')}
            </p>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} Kuehne+Nagel News - Gemini Cooperation Marks Strong First Year Performance.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a 
              href="https://mykn.kuehne-nagel.com/news/article/gemini-cooperation-hapag-first-year-maersk" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>

          {/* Navigation */}
          <div className="mt-8 flex justify-between">
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('common.backToAllArticles')}
              </Button>
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article4;